/**
 * Interface Admin - Gestion Avancée des Dépôts de Garantie
 * Dashboard complet avec métriques, filtres, groupement par owner et actions administratives
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Grid,
  CircularProgress,
  Alert,
  Button,
  Collapse,
  TextField,
  Paper,
  IconButton,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  LinearProgress,
  Avatar
} from '@mui/material';
import {
  AccountBalance as AccountBalanceIcon,
  FilterList as FilterIcon,
  ExpandLess,
  ExpandMore,
  Refresh as RefreshIcon,
  Assessment as AssessmentIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  PendingActions as PendingActionsIcon,
  Block as BlockIcon,
  Cancel as CancelIcon,
  Payment as PaymentIcon,
  Note as NotesIcon,
  Add as AddIcon,
  Euro as EuroIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Visibility as VisibilityIcon,
  MonetizationOn as MonetizationOnIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/fr';
import { useAuth } from '../../context/AuthContext';
import adminFinancialService from '../../services/adminFinancialService';
// ✅ Import du modal de détails de réservation
import ReservationDetailsModal from '../financial/ReservationDetailsModal';

// Configuration française pour dayjs
dayjs.locale('fr');

// Constantes pour les méthodes de paiement
const PAYMENT_METHOD = {
  STRIPE: 'stripe',
  CASH: 'cash',
  VIREMENT: 'virement',
  CARD: 'card',
  BANK_TRANSFER: 'bank_transfer',
  ADMIN_ADJUSTMENT: 'admin_adjustment',
  DEPOSIT_CAPTURE: 'deposit_capture',
  UNKNOWN: 'unknown' // Méthode de paiement inconnue
};

const PAYMENT_METHOD_LABELS = {
  [PAYMENT_METHOD.STRIPE]: 'Stripe',
  [PAYMENT_METHOD.CASH]: 'Espèces',
  [PAYMENT_METHOD.VIREMENT]: 'Virement',
  [PAYMENT_METHOD.CARD]: 'Carte bancaire',
  [PAYMENT_METHOD.BANK_TRANSFER]: 'Virement bancaire',
  [PAYMENT_METHOD.ADMIN_ADJUSTMENT]: 'Ajustement admin',
  [PAYMENT_METHOD.DEPOSIT_CAPTURE]: 'Capture dépôt',
  [PAYMENT_METHOD.UNKNOWN]: 'Non défini' // Label pour les méthodes non définies
};

const AdminDeposits = () => {
  const { user } = useAuth();
  
  // ================================
  // États de base
  // ================================
  const [loading, setLoading] = useState(true);
  const [overviewData, setOverviewData] = useState(null);
  const [depositsData, setDepositsData] = useState(null);
  const [error, setError] = useState(null);

  // ================================
  // États des filtres
  // ================================
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    startDate: dayjs().subtract(30, 'day'),
    endDate: dayjs(),
    status: '',
    userId: '',
    listingId: '',
    overdue: false,
    currency: '', // Filtre par devise
    paymentMethod: '' // Filtre par méthode de paiement
  });
  
  // État pour les devises et méthodes de paiement disponibles
  const [availableCurrencies, setAvailableCurrencies] = useState([]);
  const [paymentMethodStats, setPaymentMethodStats] = useState({});

  // ================================
  // États de pagination infinie
  // ================================
  const [loadedDeposits, setLoadedDeposits] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [pageSize] = useState(10); // Charger 10 dépôts à la fois
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('DESC');
  
  // ================================
  // États d'expansion des groupes
  // ================================
  const [expandedOwners, setExpandedOwners] = useState(new Set());

  // ================================
  // États des dialogues
  // ================================
  const [actionDialog, setActionDialog] = useState({ open: false, deposit: null, action: null });
  const [notesDialog, setNotesDialog] = useState({ open: false, deposit: null, notes: '' });
  
  // ✅ États pour le modal de détails de réservation
  const [selectedReservationId, setSelectedReservationId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // ================================
  // Fonctions helpers pour formatage
  // ================================
  
  /**
   * Formate un montant avec devise
   * @param {number} amount - Montant
   * @param {string} currency - Devise
   * @returns {string} Montant formaté
   */
  const formatAmount = (amount, currency = 'EUR') => {
    let symbol;
    switch (currency) {
      case 'EUR': symbol = '€'; break;
      case 'USD': symbol = '$'; break;
      case 'GBP': symbol = '£'; break;
      case 'JPY': symbol = '¥'; break;
      case 'CAD': symbol = 'C$'; break;
      case 'AUD': symbol = 'A$'; break;
      case 'CHF': symbol = 'CHF'; break;
      default: symbol = currency; break;
    }
    return `${parseFloat(amount || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} ${symbol}`;
  };

  /**
   * Formate une date
   * @param {string} dateString - Date ISO
   * @returns {string} Date formatée
   */
  const formatDate = (dateString) => {
    return dayjs(dateString).format('DD/MM/YYYY HH:mm');
  };

  /**
   * Regroupe les dépôts par propriétaire et calcule les sous-totaux
   * @param {Array} deposits - Liste des dépôts
   * @returns {Object} Dépôts groupés par owner avec sous-totaux
   */
  const groupDepositsByOwner = (deposits) => {
    if (!deposits || deposits.length === 0) return {};

    const grouped = {};
    
    deposits.forEach(deposit => {
      // Utiliser les nouvelles données enrichies du backend
      const ownerId = deposit.owner_id || 'unknown';
      const ownerName = deposit.owner_name || `Propriétaire #${ownerId}`;
      const ownerEmail = deposit.owner_email || '';
      const currency = deposit.currency || 'EUR';
      
      if (!grouped[ownerId]) {
        grouped[ownerId] = {
          owner: {
            id: ownerId,
            name: ownerName,
            email: ownerEmail
          },
          deposits: [],
          stats: {
            total_deposits: 0,
            held_count: 0,
            released_count: 0,
            refunded_count: 0,
            cancelled_count: 0,
            total_amount: 0,
            overdue_count: 0,
            currencies: [] // Nouvel attribut pour suivre les devises
          }
        };
      }
      
      grouped[ownerId].deposits.push(deposit);
      grouped[ownerId].stats.total_deposits++;
      grouped[ownerId].stats.total_amount += parseFloat(deposit.amount || 0);
      
      // Suivre les devises uniques pour ce propriétaire
      if (currency && !grouped[ownerId].stats.currencies.includes(currency)) {
        grouped[ownerId].stats.currencies.push(currency);
      }
      
      // Suivre les montants par devise
      if (!grouped[ownerId].stats.by_currency) {
        grouped[ownerId].stats.by_currency = {};
      }
      if (!grouped[ownerId].stats.by_currency[currency]) {
        grouped[ownerId].stats.by_currency[currency] = 0;
      }
      grouped[ownerId].stats.by_currency[currency] += parseFloat(deposit.amount || 0);
      
      // Suivre les méthodes de paiement
      const paymentMethod = deposit.payment_method || 'unknown';
      if (!grouped[ownerId].stats.by_payment_method) {
        grouped[ownerId].stats.by_payment_method = {};
      }
      if (!grouped[ownerId].stats.by_payment_method[paymentMethod]) {
        grouped[ownerId].stats.by_payment_method[paymentMethod] = {
          count: 0,
          amount: 0
        };
      }
      grouped[ownerId].stats.by_payment_method[paymentMethod].count++;
      grouped[ownerId].stats.by_payment_method[paymentMethod].amount += parseFloat(deposit.amount || 0);
      
      // Compter par statut
      if (deposit.status === 'held') grouped[ownerId].stats.held_count++;
      else if (deposit.status === 'released') grouped[ownerId].stats.released_count++;
      else if (deposit.status === 'refunded') grouped[ownerId].stats.refunded_count++;
      else if (deposit.status === 'cancelled') grouped[ownerId].stats.cancelled_count++;
      
      // Vérifier si en retard (plus de 3 jours après la fin de réservation)
      if (deposit.status === 'held' && deposit.end_date) {
        const endDate = dayjs(deposit.end_date);
        const now = dayjs();
        if (now.diff(endDate, 'day') > 3) {
          grouped[ownerId].stats.overdue_count++;
        }
      }
    });
    
    // Arrondir les totaux
    Object.keys(grouped).forEach(ownerId => {
      grouped[ownerId].stats.total_amount = Math.round(grouped[ownerId].stats.total_amount * 100) / 100;
    });
    
    return grouped;
  };

  /**
   * Formate les sous-totaux d'un propriétaire
   * @param {Object} ownerStats - Statistiques du propriétaire
   * @returns {JSX.Element|string} Élément formaté des totaux
   */
  const formatOwnerTotals = (ownerStats) => {
    // Si nous avons les montants détaillés par devise
    if (ownerStats.by_currency && Object.keys(ownerStats.by_currency).length > 0) {
      if (Object.keys(ownerStats.by_currency).length > 1) {
        // Plusieurs devises - afficher chaque montant séparément
        return (
          <Box>
            {Object.entries(ownerStats.by_currency).map(([currency, amount], index) => (
              <Typography key={currency} variant="body2" component="span" sx={{ 
                display: 'block', 
                fontWeight: 'medium'
              }}>
                {formatAmount(amount, currency)}
              </Typography>
            ))}
          </Box>
        );
      } else {
        // Une seule devise
        const currency = Object.keys(ownerStats.by_currency)[0];
        const amount = ownerStats.by_currency[currency];
        return formatAmount(amount, currency);
      }
    } 
    // Si nous avons seulement la liste des devises
    else if (ownerStats.currencies && ownerStats.currencies.length > 0) {
      if (ownerStats.currencies.length > 1) {
        return `${formatAmount(ownerStats.total_amount)} (Devises multiples)`;
      } else {
        return formatAmount(ownerStats.total_amount, ownerStats.currencies[0]);
      }
    }
    // Cas par défaut - pour la compatibilité avec l'ancien format
    else {
      return formatAmount(ownerStats.total_amount);
    }
  };

  /**
   * Affiche un résumé des dépôts par méthode de paiement
   * @param {Object} totalsByMethod - Totaux par méthode de paiement
   * @returns {JSX.Element} Composant React
   */
  const renderPaymentMethodSummary = (totalsByMethod) => {
    if (!totalsByMethod || Object.keys(totalsByMethod).length === 0) {
      return <Typography variant="body2">Aucune donnée de méthode de paiement disponible</Typography>;
    }
    
    return (
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {Object.keys(totalsByMethod).map(method => {
          const methodData = totalsByMethod[method];
          const methodLabel = PAYMENT_METHOD_LABELS[method] || method;
          
          return (
            <Grid item xs={12} md={6} lg={4} key={method}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {method === PAYMENT_METHOD.STRIPE ? (
                      <img src="/stripe-logo.png" alt="Stripe" style={{ height: 24, marginRight: 8 }} />
                    ) : method === PAYMENT_METHOD.CASH ? (
                      <MonetizationOnIcon sx={{ mr: 1, color: 'success.main' }} />
                    ) : method === PAYMENT_METHOD.UNKNOWN ? (
                      <WarningIcon sx={{ mr: 1, color: 'warning.main' }} />
                    ) : (
                      <PaymentIcon sx={{ mr: 1 }} />
                    )}
                    <Typography variant="h6">
                      {methodLabel}
                      {method === PAYMENT_METHOD.UNKNOWN && (
                        <Tooltip title="Ces dépôts n'ont pas de méthode de paiement définie. Vérifiez les données.">
                          <WarningIcon fontSize="small" color="warning" sx={{ ml: 1, verticalAlign: 'middle' }} />
                        </Tooltip>
                      )}
                    </Typography>
                  </Box>
                  
                  {/* Affichage du nombre de dépôts */}
                  <Typography variant="h4" gutterBottom>
                    {methodData.count} dépôt{methodData.count > 1 ? 's' : ''}
                  </Typography>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  {/* Détails par devise */}
                  <Typography variant="subtitle2" gutterBottom>
                    Montants par devise:
                  </Typography>
                  
                  {/* Afficher les montants par devise séparément */}
                  {Object.entries(methodData.by_currency).map(([currency, amount]) => (
                    <Typography variant="h5" key={currency} sx={{ mb: 1 }}>
                      {formatAmount(amount, currency)}
                    </Typography>
                  ))}
                  
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2, mb: 2 }}>
                    {Object.entries(methodData.by_currency).map(([currency, amount]) => (
                      <Chip 
                        key={currency}
                        label={`${currency}: ${formatAmount(amount, currency)}`}
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    ))}
                  </Box>
                  
                  {/* Détails par statut */}
                  <Grid container spacing={1}>
                    {Object.entries(methodData.by_status).map(([status, data]) => {
                      if (data.count === 0) return null;
                      
                      const statusColors = {
                        held: 'warning',
                        released: 'success',
                        refunded: 'info',
                        cancelled: 'error'
                      };
                      
                      return (
                        <Grid item xs={6} key={status}>
                          <Chip 
                            label={`${status} (${data.count})`}
                            color={statusColors[status]}
                            size="small"
                            variant="outlined"
                            sx={{ mb: 0.5 }}
                          />
                          {/* Afficher les montants par devise pour chaque statut */}
                          {Object.entries(data.amount).map(([currency, amount]) => (
                            <Typography variant="body2" key={currency}>
                              {formatAmount(amount, currency)}
                            </Typography>
                          ))}
                        </Grid>
                      );
                    })}
                  </Grid>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  /**
   * Calcule les totaux par méthode de paiement et par devise
   * @param {Array} deposits - Liste des dépôts
   * @returns {Object} Totaux groupés par méthode de paiement puis par devise
   */
  const calculateTotalsByPaymentMethod = (deposits) => {
    const totalsByMethod = {};
    
    if (!deposits || deposits.length === 0) {
      console.log('❌ Aucun dépôt à analyser pour les statistiques de méthode de paiement');
      return totalsByMethod;
    }
    
    console.log(`📊 Analyse de ${deposits.length} dépôts pour les statistiques de méthode de paiement`);
    
    // Premier passage pour regrouper les dépôts uniques par méthode de paiement
    // Cela évite de compter deux fois le même dépôt
    const uniqueDepositsByMethod = {};
    const methodCounts = {};
    const missingMethodDeposits = [];
    
    deposits.forEach(deposit => {
      const depositId = deposit.deposit_id || deposit.id;
      const method = deposit.payment_method || 'unknown';
      
      // Compter pour déboguer
      if (!methodCounts[method]) {
        methodCounts[method] = 0;
      }
      methodCounts[method]++;
      
      // Collecter les dépôts sans méthode de paiement définie
      if (method === 'unknown') {
        missingMethodDeposits.push({id: depositId, status: deposit.status, amount: deposit.amount});
      }
      
      if (!uniqueDepositsByMethod[method]) {
        uniqueDepositsByMethod[method] = new Map();
      }
      
      // Utiliser une Map avec le depositId comme clé pour garantir l'unicité
      uniqueDepositsByMethod[method].set(depositId, deposit);
    });
    
    // Log des méthodes de paiement trouvées
    console.log('📊 Méthodes de paiement détectées dans les dépôts:', methodCounts);
    if (missingMethodDeposits.length > 0) {
      console.log(`⚠️ ${missingMethodDeposits.length} dépôts sans méthode de paiement définie:`, missingMethodDeposits);
    }
    
    // Maintenant calculer les totaux à partir des dépôts uniques
    Object.keys(uniqueDepositsByMethod).forEach(method => {
      const deposits = Array.from(uniqueDepositsByMethod[method].values());
      console.log(`📊 Méthode ${method}: ${deposits.length} dépôts uniques`);
      
      totalsByMethod[method] = {
        count: deposits.length,
        by_currency: {},
        by_status: {
          held: { count: 0, amount: {} },
          released: { count: 0, amount: {} },
          refunded: { count: 0, amount: {} },
          cancelled: { count: 0, amount: {} }
        }
      };
      
      // Calculer les totaux par devise et par statut
      deposits.forEach(deposit => {
        const currency = deposit.currency || 'EUR';
        const amount = parseFloat(deposit.amount || 0);
        
        // Grouper par devise
        if (!totalsByMethod[method].by_currency[currency]) {
          totalsByMethod[method].by_currency[currency] = 0;
        }
        totalsByMethod[method].by_currency[currency] += amount;
        
        // Grouper par statut et devise
        if (deposit.status && totalsByMethod[method].by_status[deposit.status]) {
          totalsByMethod[method].by_status[deposit.status].count++;
          
          if (!totalsByMethod[method].by_status[deposit.status].amount[currency]) {
            totalsByMethod[method].by_status[deposit.status].amount[currency] = 0;
          }
          totalsByMethod[method].by_status[deposit.status].amount[currency] += amount;
        }
      });
    });
    
    console.log('📊 Résultat final des statistiques par méthode de paiement:', totalsByMethod);
    return totalsByMethod;
  };

  // ================================
  // Chargement initial et pagination infinie
  // ================================
  useEffect(() => {
    resetAndLoadData();
  }, [filters, sortBy, sortOrder]);

  /**
   * Remet à zéro les données et charge la première page
   */
  const resetAndLoadData = async () => {
    setLoadedDeposits([]);
    setCurrentOffset(0);
    setHasMore(true);
    await loadData(true);
  };

  const loadData = useCallback(async (reset = false) => {
    try {

      setLoading(true);
      setError(null);

      // Préparer les paramètres
      const filterParams = {
        startDate: filters.startDate?.format('YYYY-MM-DD'),
        endDate: filters.endDate?.format('YYYY-MM-DD'),
        userId: filters.userId || undefined,
        listingId: filters.listingId || undefined,
        overdue: filters.overdue,
        currency: filters.currency || undefined, // Ajout du filtre de devise
        paymentMethod: filters.paymentMethod || undefined // Ajout du filtre de méthode de paiement
      };

      const depositParams = {
        ...filterParams,
        status: filters.status || undefined,
        sortBy,
        sortOrder,
        limit: pageSize,
        page: reset ? 1 : Math.floor(currentOffset / pageSize) + 1
      };
      
      
      // Charger les données
      const [overviewResponse, depositsResponse] = await Promise.all([
        adminFinancialService.getDepositsOverview(filterParams),
        adminFinancialService.getDeposits(depositParams)
      ]);

      

      if (overviewResponse.success) {
        setOverviewData(overviewResponse.data);
        
        // Calculer les statistiques des méthodes de paiement depuis tous les dépôts chargés
        if (depositsResponse.success) {
          // Si c'est un reset, on utilise les nouveaux dépôts, sinon tous les dépôts chargés
          const depositsToUse = reset 
            ? depositsResponse.data.deposits || []
            : [...loadedDeposits, ...(depositsResponse.data.deposits || [])];
            
          if (depositsToUse.length > 0) {
            console.log(`📊 Calcul des statistiques sur ${depositsToUse.length} dépôts au total`);
            const paymentMethodStatsData = calculateTotalsByPaymentMethod(depositsToUse);
            setPaymentMethodStats(paymentMethodStatsData);
          }
        }
      } else {
        console.error('❌ Overview response failed:', overviewResponse);
      }

      if (depositsResponse.success) {
        // Gestion de l'infinite scroll : reset ou ajout de données
        if (reset) {
          // Reset complet : remplacer toutes les données
          setDepositsData(depositsResponse.data);
          setLoadedDeposits(depositsResponse.data.deposits || []);
          setCurrentOffset(pageSize);
        } else {
          // Ajout de nouvelles données pour l'infinite scroll avec déduplication
          const newDeposits = depositsResponse.data.deposits || [];
          
          // Déduplication côté frontend pour éliminer les doublons
          const existingIds = new Set(loadedDeposits.map(d => d.deposit_id || d.id));
          const uniqueNewDeposits = newDeposits.filter(deposit => {
            const id = deposit.deposit_id || deposit.id;
            return !existingIds.has(id);
          });
          
        
          
          // Ne pas ajouter si aucun nouveau dépôt unique
          if (uniqueNewDeposits.length === 0) {
            console.warn('⚠️ Aucun nouveau dépôt unique à ajouter, vérifier pagination');
            // Si la page actuelle est toujours en dessous du total, essayer la page suivante
            if (depositsResponse.data.pagination.current_page < depositsResponse.data.pagination.total_pages) {
              setCurrentOffset(prev => prev + pageSize); // Essayer la page suivante la prochaine fois
            } else {
              setHasMore(false); // Plus de pages à charger
            }
            return;
          }
          
          // Ajouter uniquement les dépôts non-dupliqués
          setLoadedDeposits(prev => [...prev, ...uniqueNewDeposits]);
          
          // Mise à jour de l'offset pour le prochain chargement
          // Utilisez la valeur de page renvoyée par le backend
          const nextOffset = depositsResponse.data.pagination.current_page * pageSize;
          setCurrentOffset(nextOffset);
          
          
          
          // Mettre à jour depositsData avec la pagination mise à jour et dédupliquée
          setDepositsData(prev => ({
            ...prev,
            ...depositsResponse.data,
            deposits: [...(prev?.deposits || []), ...uniqueNewDeposits]
          }));
        }
        
        // Mettre à jour hasMore selon le nombre total d'éléments
        const totalItems = depositsResponse.data.pagination?.total_items || 0;
        const currentLoadedCount = reset ? 
          (depositsResponse.data.deposits?.length || 0) : 
          (loadedDeposits.length + (depositsResponse.data.deposits?.length || 0));
        setHasMore(currentLoadedCount < totalItems);
      } else {
        setError(depositsResponse.error || 'Erreur lors du chargement des dépôts');
      }

    } catch (error) {
      console.error('❌ AdminDeposits.loadData: Error loading deposits data:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, [filters, currentOffset, pageSize, sortBy, sortOrder, loadedDeposits.length]);

  /**
   * Charge plus de dépôts (pagination infinie)
   */
  const loadMoreDeposits = useCallback(async () => {
    if (!hasMore || loadingMore) return;
    
    setLoadingMore(true);
    try {
      await loadData(false); // false = ajouter les données, ne pas reset
    } catch (error) {
      console.error('❌ Error loading more deposits:', error);
      setError('Erreur lors du chargement des dépôts supplémentaires');
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, loadData]);

  /**
   * Toggle l'expansion d'un propriétaire
   */
  const toggleOwnerExpansion = (ownerId) => {
    setExpandedOwners(prev => {
      const newSet = new Set(prev);
      if (newSet.has(ownerId)) {
        newSet.delete(ownerId);
      } else {
        newSet.add(ownerId);
      }
      return newSet;
    });
  };

  // ================================
  // Handlers des filtres
  // ================================
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const resetFilters = () => {
    setFilters({
      startDate: dayjs().subtract(30, 'day'),
      endDate: dayjs(),
      status: '',
      userId: '',
      listingId: '',
      overdue: false
    });
  };

  const toggleFilter = () => {
    setFilterOpen(!filterOpen);
  };

  // ================================
  // Actions sur les dépôts
  // ================================
  const handleDepositAction = async (depositId, action, data) => {
    try {
      const response = await adminFinancialService.performDepositAction(depositId, action, data);
      if (response.success) {
        setActionDialog({ open: false, deposit: null, action: null });
        await loadData(true); // Recharger les données
      } else {
        setError(response.error || 'Erreur lors de l\'action sur le dépôt');
      }
    } catch (error) {
      console.error('❌ Error performing deposit action:', error);
      setError('Erreur lors de l\'exécution de l\'action');
    }
  };
  
  // ✅ Fonctions pour gérer le modal de détails de réservation
  /**
   * Ouvre le modal de détails de réservation
   * @param {number} reservationId - ID de la réservation à afficher
   */
  const handleShowReservationDetails = (reservationId) => {
    setSelectedReservationId(reservationId);
    setIsModalOpen(true);
    console.log(`✅ Ouverture des détails de la réservation #${reservationId}`);
  };
  
  /**
   * Ferme le modal de détails de réservation
   */
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedReservationId(null);
  };

  // ================================
  // Utilitaires d'affichage
  // ================================
  const getStatusIcon = (status) => {
    const icons = {
      held: <BlockIcon />,
      released: <CheckCircleIcon />,
      refunded: <PaymentIcon />,
      cancelled: <CancelIcon />,
      pending: <PendingActionsIcon />
    };
    return icons[status] || <PendingActionsIcon />;
  };

  const getStatusColor = (status) => {
    const colors = {
      held: 'warning',
      released: 'success',
      refunded: 'info', 
      cancelled: 'default',
      pending: 'secondary'
    };
    return colors[status] || 'default';
  };

  const getStatusLabel = (status) => {
    const labels = {
      held: 'Bloqué',
      released: 'Libéré',
      refunded: 'Remboursé',
      cancelled: 'Annulé',
      pending: 'En attente'
    };
    return labels[status] || status;
  };

  // Fonction de rafraîchissement manuel forcé
  const forceRefresh = useCallback(async () => {
    console.log('🔄 FORCE REFRESH: Manuel refresh triggered');
    await resetAndLoadData();
  }, []);

  // ================================
  // RENDU DU COMPOSANT
  // ================================
  if (loading && !overviewData) {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress size={60} />
        </Box>
      </LocalizationProvider>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="fr">
      <Box sx={{ p: 3 }}>
        {/* En-tête avec titre et actions */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
            <MonetizationOnIcon sx={{ mr: 2, fontSize: 40 }} />
            Gestion des Dépôts de Garantie
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Interface administrative pour la gestion des dépôts de garantie groupés par propriétaire
          </Typography>

          {/* Boutons d'action */}
          <Box sx={{ mb: 3, display: 'flex', gap: 2, justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {overviewData && `Dernière mise à jour: ${new Date().toLocaleTimeString()}`}
            </Typography>
            
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {/* Afficher les badges de devise si plusieurs devises */}
              {overviewData?.currencies?.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1, mr: 2 }}>
                  {overviewData.currencies.map(currency => (
                    <Chip
                      key={currency}
                      label={currency}
                      color={filters.currency === currency ? "primary" : "default"}
                      size="small"
                      variant={filters.currency === currency ? "filled" : "outlined"}
                      onClick={() => handleFilterChange('currency', filters.currency === currency ? '' : currency)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              )}
              
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                endIcon={filterOpen ? <ExpandLess /> : <ExpandMore />}
                onClick={toggleFilter}
              >
                Filtres
              </Button>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={forceRefresh}
                color="primary"
                disabled={loading}
                sx={{ 
                  bgcolor: 'primary.main',
                  '&:hover': {
                    bgcolor: 'primary.dark'
                  }
                }}
              >
                {loading ? 'Actualisation...' : 'Actualiser'}
              </Button>
            </Box>
          </Box>
        </Box>

        {/* Panneau de filtres */}
        <Collapse in={filterOpen}>
          <Paper sx={{ mb: 3, p: 3, backgroundColor: '#f8f9fa', border: '1px solid #e9ecef' }}>
            <Typography variant="h6" gutterBottom>
              Filtres et recherche
            </Typography>
            
            <Grid container spacing={3}>
              {/* Période */}
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Date de début"
                  value={filters.startDate}
                  onChange={(newValue) => handleFilterChange('startDate', newValue)}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <DatePicker
                  label="Date de fin"
                  value={filters.endDate}
                  onChange={(newValue) => handleFilterChange('endDate', newValue)}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </Grid>

              {/* Statut */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Statut</InputLabel>
                  <Select
                    value={filters.status}
                    label="Statut"
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                  >
                    <MenuItem value="">Tous</MenuItem>
                    <MenuItem value="held">Bloqués</MenuItem>
                    <MenuItem value="released">Libérés</MenuItem>
                    <MenuItem value="refunded">Remboursés</MenuItem>
                    <MenuItem value="cancelled">Annulés</MenuItem>
                    <MenuItem value="pending">En attente</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* ID Utilisateur */}
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="ID Utilisateur"
                  value={filters.userId}
                  onChange={(e) => handleFilterChange('userId', e.target.value)}
                />
              </Grid>

              {/* Options */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Dépôts en retard</InputLabel>
                  <Select
                    value={filters.overdue}
                    label="Dépôts en retard"
                    onChange={(e) => handleFilterChange('overdue', e.target.value)}
                  >
                    <MenuItem value={false}>Non</MenuItem>
                    <MenuItem value={true}>Oui</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              
              {/* Filtre par Devise */}
              {overviewData?.currencies && overviewData.currencies.length > 1 && (
                <Grid item xs={12} sm={6} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Devise</InputLabel>
                    <Select
                      value={filters.currency || ""}
                      label="Devise"
                      onChange={(e) => handleFilterChange('currency', e.target.value)}
                    >
                      <MenuItem value="">Toutes</MenuItem>
                      {overviewData.currencies.map(currency => (
                        <MenuItem key={currency} value={currency}>{currency}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              
              {/* Filtre par Méthode de Paiement */}
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Méthode de Paiement</InputLabel>
                  <Select
                    value={filters.paymentMethod || ""}
                    label="Méthode de Paiement"
                    onChange={(e) => handleFilterChange('paymentMethod', e.target.value)}
                  >
                    <MenuItem value="">Toutes</MenuItem>
                    {Object.keys(PAYMENT_METHOD).map(key => (
                      <MenuItem key={key} value={PAYMENT_METHOD[key]}>
                        {PAYMENT_METHOD_LABELS[PAYMENT_METHOD[key]]}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="outlined"
                size="small"
                onClick={resetFilters}
                startIcon={<RefreshIcon />}
              >
                Réinitialiser
              </Button>
            </Box>
          </Paper>
        </Collapse>

        {/* Affichage des erreurs */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {/* Dashboard - Vue d'ensemble */}
        {overviewData && (() => {
          // Regrouper par devise si la réponse contient des données par devise
          if (overviewData.by_currency && Object.keys(overviewData.by_currency).length > 0) {
            return (
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Vue d'ensemble par devise
                </Typography>
                <Grid container spacing={2}>
                  {Object.entries(overviewData.by_currency).map(([currency, data]) => (
                    <Grid item xs={12} md={4} lg={3} key={currency}>
                      <Card>
                        <CardHeader 
                          title={`${currency}`} 
                          titleTypographyProps={{ variant: 'h6' }}
                          avatar={
                            <Avatar sx={{ bgcolor: currency === 'EUR' ? 'primary.main' : 
                                       currency === 'USD' ? 'success.main' : 'secondary.main' }}>
                              {currency.substring(0, 1)}
                            </Avatar>
                          }
                        />
                        <CardContent>
                          <Grid container spacing={1}>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Total</Typography>
                              <Typography variant="body1" fontWeight="bold">
                                {formatAmount(data.total_amount || 0, currency)}
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Dépôts</Typography>
                              <Typography variant="body1">{data.total_deposits || 0}</Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Bloqués</Typography>
                              <Typography variant="body1">
                                {formatAmount(data.held_amount || 0, currency)} ({data.held_count || 0})
                              </Typography>
                            </Grid>
                            <Grid item xs={6}>
                              <Typography variant="caption" color="text.secondary">Remboursés</Typography>
                              <Typography variant="body1">
                                {formatAmount(data.refunded_amount || 0, currency)} ({data.refunded_count || 0})
                              </Typography>
                            </Grid>
                          </Grid>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            );
          }
          return null;
        })()}
        
        {overviewData && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {/* Métriques principales */}
            <Grid item xs={12} md={3}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white' 
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <EuroIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">Total Dépôts</Typography>
                  </Box>
                  <Typography variant="h4">
                    {overviewData.total_by_currency ? (
                      Object.entries(overviewData.total_by_currency).map(([currency, amount], index) => (
                        <Box component="span" key={currency} sx={{ display: 'block' }}>
                          {formatAmount(amount || 0, currency)}
                        </Box>
                      ))
                    ) : (
                      formatAmount(overviewData.total_amount || 0)
                    )}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    {overviewData.total_deposits || 0} dépôts
                    {overviewData.currencies && overviewData.currencies.length > 0 && (
                      <Box component="span" sx={{ ml: 1, fontStyle: 'italic' }}>
                        ({overviewData.currencies.join(', ')})
                      </Box>
                    )}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white' 
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <WarningIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">Bloqués</Typography>
                  </Box>
                  <Typography variant="h4">{overviewData.held_count || 0}</Typography>
                  <Typography variant="h6" sx={{ mt: 1 }}>
                    {overviewData.held_by_currency ? (
                      Object.entries(overviewData.held_by_currency).map(([currency, amount], index) => (
                        <Box component="span" key={currency} sx={{ display: 'block' }}>
                          {formatAmount(amount || 0, currency)}
                        </Box>
                      ))
                    ) : (
                      formatAmount(overviewData.held_amount || 0)
                    )}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white' 
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <PaymentIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">Remboursés</Typography>
                  </Box>
                  <Typography variant="h4">{overviewData.refunded_count || 0}</Typography>
                  <Typography variant="h6" sx={{ mt: 1 }}>
                    {overviewData.refunded_by_currency ? (
                      Object.entries(overviewData.refunded_by_currency).map(([currency, amount], index) => (
                        <Box component="span" key={currency} sx={{ display: 'block' }}>
                          {formatAmount(amount || 0, currency)}
                        </Box>
                      ))
                    ) : (
                      formatAmount(overviewData.refunded_amount || 0)
                    )}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                color: 'white' 
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CheckCircleIcon sx={{ mr: 1 }} />
                    <Typography variant="h6">Libérés</Typography>
                  </Box>
                  <Typography variant="h4">{overviewData.released_count || 0}</Typography>
                  <Typography variant="h6" sx={{ mt: 1 }}>
                    {overviewData.released_by_currency ? (
                      Object.entries(overviewData.released_by_currency).map(([currency, amount], index) => (
                        <Box component="span" key={currency} sx={{ display: 'block' }}>
                          {formatAmount(amount || 0, currency)}
                        </Box>
                      ))
                    ) : (
                      formatAmount(overviewData.released_amount || 0)
                    )}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* Statistiques par méthode de paiement */}
        {Object.keys(paymentMethodStats).length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Statistiques par méthode de paiement
            </Typography>
            {renderPaymentMethodSummary(paymentMethodStats)}
          </Box>
        )}

        {/* Liste des dépôts groupés par propriétaire */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">
                Liste des Dépôts par Propriétaire ({loadedDeposits.length} chargés)
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {(() => {
                  const groupedData = groupDepositsByOwner(loadedDeposits);
                  const ownerCount = Object.keys(groupedData).length;
                  return (
                    <>
                      {ownerCount > 0 && (
                        <Typography variant="body2" color="text.secondary">
                          {ownerCount} propriétaire{ownerCount > 1 ? 's' : ''}
                        </Typography>
                      )}
                    </>
                  );
                })()}
                {loading && <CircularProgress size={24} />}
              </Box>
            </Box>

            {/* Affichage groupé par owner */}
            {(() => {
              const groupedData = groupDepositsByOwner(loadedDeposits);
              return Object.values(groupedData).map((ownerGroup) => (
                <Card key={ownerGroup.owner.id} sx={{ mb: 2 }}>
                  <CardHeader
                    avatar={
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {ownerGroup.owner.name.charAt(0).toUpperCase()}
                      </Avatar>
                    }
                    title={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="h6">{ownerGroup.owner.name}</Typography>
                        <Chip 
                          label={`${ownerGroup.stats.total_deposits} dépôt${ownerGroup.stats.total_deposits > 1 ? 's' : ''}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </Box>
                    }
                    subheader={
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          {ownerGroup.owner.email}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1, fontWeight: 'medium' }}>
                          Total: {formatOwnerTotals(ownerGroup.stats)}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          {ownerGroup.stats.held_count > 0 && (
                            <Chip 
                              label={`${ownerGroup.stats.held_count} bloqué${ownerGroup.stats.held_count > 1 ? 's' : ''}`}
                              size="small"
                              color="warning"
                              sx={{ mr: 1, mb: 0.5 }}
                            />
                          )}
                          {ownerGroup.stats.released_count > 0 && (
                            <Chip 
                              label={`${ownerGroup.stats.released_count} libéré${ownerGroup.stats.released_count > 1 ? 's' : ''}`}
                              size="small"
                              color="success"
                              sx={{ mr: 1, mb: 0.5 }}
                            />
                          )}
                          {ownerGroup.stats.refunded_count > 0 && (
                            <Chip 
                              label={`${ownerGroup.stats.refunded_count} remboursé${ownerGroup.stats.refunded_count > 1 ? 's' : ''}`}
                              size="small"
                              color="info"
                              sx={{ mr: 1, mb: 0.5 }}
                            />
                          )}
                          {ownerGroup.stats.overdue_count > 0 && (
                            <Chip 
                              label={`${ownerGroup.stats.overdue_count} en retard`}
                              size="small"
                              color="error"
                              sx={{ mr: 1, mb: 0.5 }}
                            />
                          )}
                        </Box>
                      </Box>
                    }
                    action={
                      <IconButton 
                        onClick={() => toggleOwnerExpansion(ownerGroup.owner.id)}
                        aria-expanded={expandedOwners.has(ownerGroup.owner.id)}
                      >
                        {expandedOwners.has(ownerGroup.owner.id) ? <ExpandLess /> : <ExpandMore />}
                      </IconButton>
                    }
                  />
                  <Collapse in={expandedOwners.has(ownerGroup.owner.id)} timeout="auto" unmountOnExit>
                    <CardContent sx={{ pt: 0 }}>
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>ID Dépôt</TableCell>
                              <TableCell>Réservation</TableCell>
                              <TableCell>Locataire</TableCell>
                              <TableCell>Montant</TableCell>
                              <TableCell>Devise</TableCell>
                              <TableCell>Méthode</TableCell>
                              <TableCell>Statut</TableCell>
                              <TableCell>Date création</TableCell>
                              <TableCell>Actions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {ownerGroup.deposits.map((deposit) => (
                              <TableRow key={deposit.id}>
                                <TableCell>
                                  <Chip 
                                    label={`#${deposit.id}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Box>
                                    <Typography variant="body2" fontWeight="bold">
                                      #{deposit.reservation_id}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {deposit.listing_title || 'Titre non disponible'}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Box>
                                    <Typography variant="body2">
                                      {deposit.renter_name || 'N/A'}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {deposit.renter_email || 'N/A'}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2" fontWeight="bold">
                                    {formatAmount(deposit.amount, deposit.currency)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Chip 
                                    label={deposit.currency || 'EUR'}
                                    size="small"
                                    color="default"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={PAYMENT_METHOD_LABELS[deposit.payment_method] || deposit.payment_method || 'Non spécifié'}
                                    size="small"
                                    color={deposit.payment_method === PAYMENT_METHOD.STRIPE ? 'primary' : 
                                           deposit.payment_method === PAYMENT_METHOD.CASH ? 'success' : 'default'}
                                    variant="outlined"
                                    icon={deposit.payment_method === PAYMENT_METHOD.CASH ? <MonetizationOnIcon /> : 
                                          deposit.payment_method === PAYMENT_METHOD.STRIPE ? <PaymentIcon /> : null}
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    icon={getStatusIcon(deposit.status)}
                                    label={getStatusLabel(deposit.status)}
                                    color={getStatusColor(deposit.status)}
                                    size="small"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Typography variant="body2">
                                    {formatDate(deposit.created_at)}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box sx={{ display: 'flex', gap: 1 }}>
                                    {deposit.status === 'held' && (
                                      <Tooltip title="Rembourser">
                                        <IconButton
                                          size="small"
                                          onClick={() => setActionDialog({ 
                                            open: true, 
                                            deposit, 
                                            action: 'refund' 
                                          })}
                                        >
                                          <PaymentIcon />
                                        </IconButton>
                                      </Tooltip>
                                    )}
                                    <Tooltip title="Voir notes">
                                      <IconButton
                                        size="small"
                                        onClick={() => setNotesDialog({ 
                                          open: true, 
                                          deposit, 
                                          notes: deposit.admin_notes || '' 
                                        })}
                                      >
                                        <NotesIcon />
                                      </IconButton>
                                    </Tooltip>
                                    {/* ✅ Bouton pour voir les détails de la réservation */}
                                    <Tooltip title="Voir la réservation">
                                      <IconButton
                                        size="small"
                                        color="primary"
                                        onClick={() => handleShowReservationDetails(deposit.reservation_id)}
                                      >
                                        <VisibilityIcon />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </CardContent>
                  </Collapse>
                </Card>
              ));
            })()}

            {/* Load More Button for Infinite Scroll */}
            {hasMore && !loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button 
                  variant="outlined" 
                  onClick={loadMoreDeposits}
                  disabled={loadingMore}
                  startIcon={loadingMore ? <CircularProgress size={16} /> : null}
                >
                  {loadingMore ? 'Chargement...' : 'Charger plus de dépôts'}
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Dialog - Action sur dépôt */}
        <Dialog 
          open={actionDialog.open} 
          onClose={() => setActionDialog({ open: false, deposit: null, action: null })}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Action sur le dépôt #{actionDialog.deposit?.id}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>
              Montant: {formatAmount(actionDialog.deposit?.amount, actionDialog.deposit?.currency)}
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes administratives"
              sx={{ mt: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setActionDialog({ open: false, deposit: null, action: null })}>
              Annuler
            </Button>
            <Button 
              variant="contained" 
              onClick={() => handleDepositAction(actionDialog.deposit?.id, actionDialog.action, {})}
            >
              Confirmer
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog - Détails dépôt */}
        <Dialog 
          open={notesDialog.open} 
          onClose={() => setNotesDialog({ open: false, deposit: null, notes: '' })}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            Détails du dépôt #{notesDialog.deposit?.id}
          </DialogTitle>
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2">Montant:</Typography>
                <Typography variant="body1">
                  {formatAmount(notesDialog.deposit?.amount, notesDialog.deposit?.currency)}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2">Statut:</Typography>
                <Chip
                  icon={getStatusIcon(notesDialog.deposit?.status)}
                  label={getStatusLabel(notesDialog.deposit?.status)}
                  color={getStatusColor(notesDialog.deposit?.status)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Réservation:</Typography>
                <Typography variant="body1">
                  #{notesDialog.deposit?.reservation?.id} - {notesDialog.deposit?.reservation?.Listing?.title}
                </Typography>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNotesDialog({ open: false, deposit: null, notes: '' })}>
              Fermer
            </Button>
          </DialogActions>
        </Dialog>
        {/* ✅ Modal de détails de réservation */}
        <ReservationDetailsModal
          open={isModalOpen}
          onClose={handleCloseModal}
          reservationId={selectedReservationId}
        />
      </Box>
    </LocalizationProvider>
  );
};

export default AdminDeposits;